import React, { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { v4 } from "uuid";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { Product, ProductReservation, ProductType } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { activeUser } from "../../services/AuthService";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import {
  getAllProductReservations,
  getOneProductReservation,
  deleteOneProductReservation,
} from "../../services/ProductReservationService";
import { NewProductReservationForm } from "../../forms/NewProductReservationForm/NewProductReservationForm";
import { Utility } from "../../core/Utility";
import "./ProductReservationPage.scss";

export const ProductReservationPage = () => {
  const { t } = useTranslation();
  const toast = useRef<any>(null);

  const { state, selectedDateFromSet, selectedDateToSet } =
    useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear(); // Poslovna godina
  const yearStart = new Date(businessYear, 0, 1); // Početak godine
  const yearEnd = new Date(businessYear, 11, 31); // Kraj godine

  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // Za tekuću godinu, postavi datume od 1. januara do danas
      const from = new Date(today.getFullYear(), 0, 1);
      const to = today;
      return { from, to };
    } else {
      // Za prethodne godine, postavi ceo opseg te godine
      return { from: yearStart, to: yearEnd };
    }
  };
  const [dateFrom, setDateFrom] = useState<Date>(
    () => getDefaultRangeForBusinessYear().from,
  );
  const [dateTo, setDateTo] = useState<Date>(
    () => getDefaultRangeForBusinessYear().to,
  );

  const [processing, setProcessing] = useState(false);
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [reservations, setReservations] = useState<ProductReservation[]>([]);

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>(
    activeUser?.role?.name === RoleType.Warehouseman
      ? (activeUser?.warehouse as Warehouse)
      : ({} as Warehouse),
  );
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product,
  );
  const [selectedProductReservation, setSelectedProductReservation] =
    useState<ProductReservation>();
  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [idToDelete, setIdToDelete] = useState<string>("");
  const [showForm, setShowForm] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    reservationNumberFormatted: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    productName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    warehouseName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    note: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);

  const canDelete =
    activeUser?.role?.name === RoleType.Superadmin ||
    activeUser?.role?.name === RoleType.Director;

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, pResp] = await Promise.all([
      getAllWarehouses(),
      getAllProducts(),
    ]);
    ensureSuccess(wResp);
    ensureSuccess(pResp);

    const wh: Warehouse[] = wResp?.data?.result ?? [];
    const pr: Product[] = pResp?.data?.result ?? [];

    setWarehousesApi(wh);
    setProductsApi(pr);

    return { wh, pr };
  };

  const refreshReservations = async () => {
    setProcessing(true);
    try {
      const resp = await getAllProductReservations({
        year: businessYear,
        dateFrom,
        dateTo,
      });
      ensureSuccess(resp);

      let list: ProductReservation[] = resp?.data?.result ?? [];

      list = list.filter(
        (r: any) => r?.product?.type === ProductType.MercantileGoods,
      );

      if (selectedWarehouse?.id) {
        list = list.filter(
          (r: any) => r?.warehouse?.id == selectedWarehouse.id,
        );
      }

      if (selectedProduct?.id) {
        list = list.filter((r: any) => r?.product?.id == selectedProduct.id);
      }

      if (activeUser?.role?.name === RoleType.Commercialist) {
        const fullName =
          `${activeUser?.firstName ?? ""} ${activeUser?.lastName ?? ""}`.trim();
        list = list.filter((r: any) => r?.partner?.commercialist1 === fullName);
      }

      const normalized: any[] = list.map((r: any) => ({
        ...r,
        productName: r?.product?.name ?? "",
        warehouseName: r?.warehouse?.name ?? "",
        partnerName: r?.partner?.name ?? "",
      }));

      setReservations(normalized as ProductReservation[]);
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri učitavanju rezervacija")),
        life: 3500,
      });
      setReservations([]);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        await refreshLookups();
      } catch (e: any) {
        console.error(e);
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ??
            String(t("Greška pri učitavanju šifarnika")),
          life: 3500,
        });
        setWarehousesApi([]);
        setProductsApi([]);
      } finally {
        setProcessing(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    selectedDateFromSet(from);
    selectedDateToSet(to);
  }, [businessYear]);

  useEffect(() => {
    // kad se promene filteri u headeru, osveži listu
    refreshReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    businessYear,
    dateFrom,
    dateTo,
    selectedWarehouse?.id,
    selectedProduct?.id,
  ]);

  const onDateFromChange = (property: string, value: Date | null) => {
    setDateFrom(value as Date);
  };

  const onDateToChange = (property: string, value: Date | null) => {
    setDateTo(value as Date);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    const wh = warehousesApi.find((w) => w.id == value) as Warehouse;
    setSelectedWarehouse(wh ?? ({} as Warehouse));
  };

  const handleProductChange = (prop: string, value: any) => {
    const pr = productsApi.find((p) => p.id == value) as Product;
    setSelectedProduct(pr ?? ({} as Product));
  };

  const openEdit = async (row: ProductReservation) => {
    try {
      const id = String((row as any)?.id ?? "");
      if (!id) return;

      const resp = await getOneProductReservation(id);
      ensureSuccess(resp);

      const pr = resp?.data?.result as ProductReservation;
      setSelectedProductReservation(cloneDeep(pr));
      setShowForm(v4());
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri učitavanju rezervacije")),
        life: 3500,
      });
    }
  };

  const acceptDelete = async () => {
    if (!idToDelete) return;

    try {
      const resp = await deleteOneProductReservation(idToDelete);
      ensureSuccess(resp);

      toast.current?.show({
        severity: "success",
        detail: String(t("Rezervacija je obrisana")),
        life: 3000,
      });

      setDeleteConfirmVisible(false);
      setIdToDelete("");
      await refreshReservations();
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri brisanju rezervacije")),
        life: 3500,
      });
      setDeleteConfirmVisible(false);
    }
  };

  const tableValue = useMemo(() => reservations, [reservations]);

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const dateBody = (row: any) =>
    row?.date ? <span>{Utility.formatDate(new Date(row.date))}</span> : <></>;
  const expiryBody = (row: any) =>
    row?.expiryDate ? (
      <span>{Utility.formatDate(new Date(row.expiryDate))}</span>
    ) : (
      <></>
    );

  const typeBody = (row: any) =>
    row?.type === ProductType.MercantileGoods
      ? t("Merkantila")
      : t("Repromaterijal");

  const amountBody = (row: any) =>
    row?.amount ? Utility.addThousandSeparator(row.amount) : "";

  const actionsBody = (row: ProductReservation) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => openEdit(row)}
        />

        {canDelete && (
          <ButtonInput
            addStyle={{
              border: "none",
              background: "#198754",
              marginLeft: "1rem",
            }}
            labelText={""}
            icon="bi bi-trash"
            onButtonClick={() => {
              const id = String((row as any)?.id ?? "");
              setIdToDelete(id);
              setDeleteConfirmVisible(true);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
      </div>
    );
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes"
              style={{ fontSize: 14, marginLeft: "1rem", marginRight: "1rem" }}
            />
            <span className="header-left-title">
              {t("Rezervacije merkantile")}
            </span>

            <span className="header-elements">{t("za")}</span>
            <DropdownInput
              id="product"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={productsApi.filter(
                (p) => p.type === ProductType.MercantileGoods,
              )}
              filter
              labelText=""
              placeholderText={t("Proizvod")}
              control={control}
              setValue={setValue}
              errors={errors}
            />

            <span className="header-elements">{t("od")}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
            />

            <span className="header-elements">{t("do")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
            />

            <span className="header-elements">{t("u")}</span>
            <DropdownInput
              id="warehouse"
              value={selectedWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehousesApi.filter((w) => w.virtual !== true)}
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={activeUser?.role?.name === RoleType.Warehouseman}
            />
          </div>

          <div className="header-right">
            <NewProductReservationForm
              buttonLabel={String(t("Kreiraj rezervaciju"))}
              buttonIcon="bi bi-boxes"
              show={showForm}
              productReservation={selectedProductReservation}
              reservationType={ProductType.MercantileGoods}
              buttonStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                marginRight: "1rem",
              }}
              buttonSaveStyle={{
                border: "none",
                background: "rgba(0, 165, 83, 1)",
              }}
              buttonResetStyle={{
                border: "none",
                background: "rgba(220, 53, 69, 1)",
              }}
              refreshingData={async () => {
                setSelectedProductReservation(undefined);
                await refreshReservations();
              }}
            />
          </div>
        </div>

        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              value={tableValue}
              dataKey="id"
              scrollable
              scrollHeight="80vh"
              stripedRows
              showGridlines
              removableSort
              sortMode="single"
              filterDisplay="row"
              filters={filters}
              onFilter={(e) => {
                setFilters(e.filters as DataTableFilterMeta);
                setFirst(0);
              }}
              paginator
              first={first}
              rows={rows}
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate=""
              emptyMessage={t("Nema rezervacija za zadate kriterijume")}
            >
              <Column
                field="date"
                header={t("Datum rezervacije")}
                body={dateBody}
                sortable
                style={{ minWidth: "170px" }}
              />

              <Column
                field="reservationNumberFormatted"
                header={t("Broj rezervacije")}
                sortable
                filter
                style={{ minWidth: "200px" }}
              />

              <Column
                field="productName"
                header={t("Naziv robe")}
                sortable
                filter
                style={{ minWidth: "280px" }}
              />

              <Column
                field="type"
                header={t("Tip robe")}
                body={typeBody}
                sortable
                style={{ minWidth: "160px" }}
              />

              <Column
                field="warehouseName"
                header={t("Magacin")}
                sortable
                filter
                style={{ minWidth: "220px" }}
              />

              <Column
                field="amount"
                header={t("Količina")}
                body={amountBody}
                sortable
                style={{ minWidth: "160px" }}
              />

              <Column
                field="partnerName"
                header={t("Kupac")}
                sortable
                filter
                style={{ minWidth: "260px" }}
              />

              <Column
                field="expiryDate"
                header={t("Datum isteka")}
                body={expiryBody}
                sortable
                style={{ minWidth: "170px" }}
              />

              <Column
                field="note"
                header={t("Napomena")}
                sortable
                filter
                style={{ minWidth: "260px" }}
              />

              <Column
                header={t("Opcije")}
                body={actionsBody}
                style={{ minWidth: "140px", textAlign: "center" }}
              />
            </DataTable>
          </div>
        )}
      </div>

      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t("Da li ste sigurni da želite da obrišete rezervaciju?")}
        header={t("Potvrda brisanja rezervacije")}
        icon="bi bi-exclamation-circle"
        acceptLabel={String(t("Da"))}
        acceptClassName="accept-button"
        accept={acceptDelete}
        rejectLabel={String(t("Ne"))}
        rejectClassName="reject-button"
        reject={() => {
          setDeleteConfirmVisible(false);
          setIdToDelete("");
        }}
      />
    </PageLayout>
  );
};
